import { useState } from 'react';

const FunnelIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PeriodSelector = ({ period, onChange }) => {
  const periods = [
    { value: '7days', label: '7 дней' },
    { value: '30days', label: '30 дней' },
    { value: '90days', label: '90 дней' },
  ];

  return (
    <div className="flex gap-2">
      {periods.map((p) => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            period === p.value
              ? 'bg-accent-primary text-white'
              : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
};

export const ActivationFunnel = ({ funnel, isLoading, period, onPeriodChange }) => {
  const stages = [
    { key: 'registered', icon: '👤', color: '#8b5cf6' },
    { key: 'created_first_chat', icon: '💬', color: '#7c3aed' },
    { key: 'sent_first_message', icon: '✉️', color: '#6d28d9' },
    { key: 'received_first_response', icon: '🤖', color: '#5b21b6' },
    { key: 'continued_usage', icon: '🔄', color: '#4c1d95' },
    { key: 'day1_retention', icon: '📅', color: '#3b0764' },
  ];

  if (isLoading || !funnel) {
    return (
      <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="text-accent-primary">
              <FunnelIcon />
            </div>
            <h2 className="text-xl font-bold text-text-primary">Воронка активации новых пользователей</h2>
          </div>
          <div className="h-8 w-48 bg-bg-tertiary rounded animate-pulse" />
        </div>

        <div className="space-y-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 w-48 bg-bg-tertiary rounded mb-2" />
              <div className="h-3 w-full bg-bg-tertiary rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const maxValue = funnel.registered.count;

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="text-accent-primary">
            <FunnelIcon />
          </div>
          <div>
            <h2 className="text-xl font-bold text-text-primary">Воронка активации новых пользователей</h2>
            <p className="text-xs text-text-muted mt-1">
              Показывает, где новые пользователи отваливаются в первый день
            </p>
          </div>
        </div>
        <PeriodSelector period={period} onChange={onPeriodChange} />
      </div>

      {/* Основные метрики */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-bg-tertiary rounded-lg p-4">
          <div className="text-xs text-text-muted mb-1">Всего зарегистрировалось</div>
          <div className="text-2xl font-bold text-text-primary">{funnel.registered.count}</div>
        </div>
        <div className="bg-bg-tertiary rounded-lg p-4">
          <div className="text-xs text-text-muted mb-1">Успешная активация</div>
          <div className="text-2xl font-bold text-success">{funnel.activation_rate}%</div>
          <div className="text-xs text-text-muted">{funnel.total_activated} пользователей</div>
        </div>
      </div>

      {/* Воронка */}
      <div className="space-y-4">
        {stages.map((stage, index) => {
          const data = funnel[stage.key];
          const count = data?.count || 0;
          const conversion = data?.conversion || 0;
          const stepConversion = data?.step_conversion || 0;
          const width = maxValue > 0 ? (count / maxValue) * 100 : 0;

          return (
            <div key={stage.key}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{stage.icon}</span>
                  <span className="text-sm text-text-secondary">{data.label}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-semibold text-text-primary">{count}</span>
                  <span className="text-xs text-text-muted ml-2">({conversion}%)</span>
                  {index > 0 && (
                    <div className="text-xs text-accent-primary mt-0.5">
                      ↓ {stepConversion}%
                    </div>
                  )}
                </div>
              </div>
              <div className="w-full bg-bg-tertiary rounded-full h-4 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${width}%`, backgroundColor: stage.color }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Анализ проблемных зон */}
      <div className="mt-6 p-4 bg-error/10 border border-error/30 rounded-lg">
        <h3 className="text-sm font-semibold text-error mb-2">⚠️ Проблемные зоны</h3>
        <div className="space-y-2 text-sm text-text-secondary">
          {(() => {
            const issues = [];
            
            const chatStepConversion = funnel.created_first_chat.step_conversion;
            if (chatStepConversion < 70) {
              issues.push(`Только ${chatStepConversion}% новых пользователей создают первый чат. Возможно, они не понимают, как начать.`);
            }

            const messageStepConversion = funnel.sent_first_message.step_conversion;
            if (messageStepConversion < 80) {
              issues.push(`${messageStepConversion}% пользователей отправили сообщение после создания чата. Проверьте UX ввода сообщений.`);
            }

            const responseStepConversion = funnel.received_first_response.step_conversion;
            if (responseStepConversion < 90) {
              issues.push(`Только ${responseStepConversion}% получили ответ от AI. Возможны технические проблемы.`);
            }

            const day1Conversion = funnel.day1_retention.conversion;
            if (day1Conversion < 30) {
              issues.push(`Day-1 Retention всего ${day1Conversion}%. Пользователи не возвращаются — нужна система удержания.`);
            }

            if (issues.length === 0) {
              return <p className="text-success">✅ Воронка активации выглядит здоровой!</p>;
            }

            return issues.map((issue, i) => (
              <p key={i} className="text-text-secondary">• {issue}</p>
            ));
          })()}
        </div>
      </div>
    </div>
  );
};
